import axios from 'axios';
import { CONFIG } from 'src/config-global';
import { logError } from 'src/services/errorLogger';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: CONFIG.serverUrl });

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const requestUrl = error?.config?.url || '';

    if (status >= 500 && !requestUrl.includes(endpoints.system_errors)) {
      logError(
        {
          type: 'api_error',
          message: `API request failed with status ${status}`,
          stack_trace: error?.stack || '',
          meta: {
            status,
            api_url: requestUrl.split('?')[0],
            api_method: error?.config?.method || '',
          },
        },
        endpoints.system_errors
      );
    }

    return Promise.reject((error.response && error.response.data) || 'Something went wrong!');
  }
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];

    const res = await axiosInstance.get(url, { ...config });

    return res.data;
  } catch (error) {
    console.error('Failed to fetch:', error);
    throw error;
  }
};

// ----------------------------------------------------------------------

export const endpoints = {
  chat: 'https://api-dev-minimal-v610.pages.dev/api/chat',
  kanban: '/api/kanban',
  calendar: '/api/calendar',
  auth: {
    me: '/api/v1/users/get_user_by_id',
    signIn: '/api/v1/users/login',
    signUp: '/api/v1/users/sign_up',
    verify: '/api/v1/users/verify_otp',
    sendForget: '/api/v1/users/forgot_password',
    forgetVerify: '/api/v1/users/verify_forgot_password',
    updateForgetPassword: '/api/v1/users/change_forgot_password',
    resend_otp: '/api/v1/users/resend_otp',
    validateReferral: '/api/v1/users/referrals-validate',
    saveReferral: '/api/v1/users/referrals/save',
    markFirstVisit: '/api/v1/users/mark_first_visit',
  },
  mail: {
    list: '/api/mail/list',
    details: '/api/mail/details',
    labels: '/api/mail/labels',
  },
  referral: {
    referral_summary: '/api/v1/users/referrals/summary',
    referral_history: '/api/v1/users/referrals/history',
    referral_detail: '/api/v1/users/referrals/get_referral_detail',
    shopify_connect: '/api/v1/users/shopify-connect',
    shopify_disconnect: '/api/v1/users/shopify-disconnect',
  },
  // post: {
  //   list: '/api/post/list',
  //   details: '/api/post/details',
  //   latest: '/api/post/latest',
  //   search: '/api/post/search',
  // },
  product: {
    list: '/api/product/list',
    details: '/api/product/details',
    search: '/api/product/search',
  },
  profile: {
    profileUpdate: '/api/v1/users/update_profile',
    changeMedia: '/api/v1/users/upload_user_media',
    deleteAccount: '/api/v1/users/delete_account',
    disableAccount: '/api/v1/users/disable_account',
    changePassword: '/api/v1/users/change_password',
    changeEmailRequest: '/api/v1/users/change_email_otp',
    changeMessageRequest: '/api/v1/users/toggle_allow_private_message',
    verifyEmail: '/api/v1/users/verify_otp',
    changeEmail: '/api/v1/users/change_email',
  },
  gym: {
    profileUpdate: '/api/v1/gym/update_gym_profile',
    changeMedia: '/api/v1/users/upload_user_media',
    deleteAccount: '/api/v1/users/delete_account',
    getGyms: '/api/v1/gym/get_gyms',
    get_gym_users_for_dropdown: '/api/v1/gym/get_gym_users_for_dropdown',
    get_chat_history: '/api/v1/users/get_chat_history',
    get_gym_services: '/api/v1/gym/get_gym_services',
    add_gym_service: '/api/v1/gym/add_gym_service',
    changeStatus: '/api/v1/gym/action_gym_status',
  },
  trainer: {
    add_trainer: '/api/v1/trainer/create_trainer',
    add_update_slots: '/api/v1/trainer/add_update_slots',
    validate_slots: '/api/v1/training_classes/validate_date_time',

    delete_availability_slot: '/api/v1/trainer/delete_availability_slot',
    get_Trainers: '/api/v1/trainer/get_trainers',
    get_slots: '/api/v1/trainer/get_slots',
    get_booking_list: '/api/v1/trainer/get_booking_list',
    get_booked_slots_by_id: '/api/v1/trainer/get_booked_slots_by_id',
    get_time_slots_by_id: '/api/v1/trainer/get_time_slots_by_id',
    delete_trainer: '/api/v1/trainer/delete_account',
    confirm_delete_trainer: '/api/v1/users/check_group_admin',
    enable_disable_account: '/api/v1/trainer/enable_disable_account',
    get_trainers_dropdown: '/api/v1/trainer/get_trainers_dropdown',
    changeStatus: '/api/v1/trainer/action_trainer_status',
    add_trainer_service: '/api/v1/trainer/add_trainer_service',
    get_trainer_services: '/api/v1/trainer/get_trainer_services',
    get_personal_trainers: '/api/v1/personal_member/get_trainers',
  },
  members: {
    add_members: '/api/v1/member/create_member',
    book_a_appointment: '/api/v1/member/book_a_appointment',
    get_members: '/api/v1/member/get_members',
    delete_members: '/api/v1/member/delete_account',
    enable_disable_account: '/api/v1/member/enable_disable_account',
    assignTrainers: '/api/v1/member/assign_trainers',
    changeStatus: '/api/v1/member/action_member_status',
    send_request: '/api/v1/member/member_request_to_trainer',
    send_personal_request: '/api/v1/personal_member/personal_member_request_to_trainer',
    accept_reject_request: '/api/v1/member/action_on_request_status',
    accept_reject_personal_request: '/api/v1/personal_member/action_on_request_status',

    get_members_dropdown: '/api/v1/member/get_members_dropdown',
    get_non_invited_members: '/api/v1/training_classes/get_non_invited_members',
    get_personal_members: '/api/v1/personal_member/get_personal_member',
    get_personal_profile: '/api/v1/personal_member/get_personal_member_detail',
  },
  personalMembers: {
    get_personal_member: '/api/v1/personal_member/get_personal_member',
    action_on_personal_member_request_status:
      '/api/v1/personal_member/action_on_personal_member_request_status',
  },
  adminPlaylist: {
    get_playlists: '/api/v1/playlist/get_playlists',
    delete_playlists: '/api/v1/playlist/delete_playlist',
  },
  adminVideolist: {
    get_videos: '/api/v1/video/get_videos',
    delete_video: '/api/v1/video/delete_video',
    get_playlists: '/api/v1/playlist/get_playlists',
    delete_playlists: '/api/v1/playlist/delete_playlist',
  },
  adminPostlist: {
    delete_post: '/api/v1/post/delete_post',
    delete_comment: '/api/v1/post/delete_comment',
    get_post: '/api/v1/post/get_posts',
    get_Comments_On_Post: '/api/v1/post/get_post_comments',
    get_post_likes: '/api/v1/post/get_post_likes',
  },
  adminGrouplist: {
    get_Groups: '/api/v1/gym/get_groups_by_gym_id',
    get_group_chat: '/api/v1/users/get_group_chat',
    remove_group_member: '/api/v1/member/remove_group_member',
  },
  adminModerationList: {
    get_moderation_history: '/api/v1/moderation/get_moderation_history',
    action_on_moderation: '/api/v1/moderation/action_on_moderation',
  },

  video: {
    getplaylist: '/api/v1/playlist/get_playlists',
    getplaylistdropdown: '/api/v1/playlist/get_playlists_dropdown',
    add_video: '/api/v1/video/upload_video',
    create_playlist: '/api/v1/playlist/create_playlist',
    delete_playlist: '/api/v1/playlist/delete_playlist',
    getplaylistbyid: '/api/v1/playlist/get_plylist_by_id',
    edit_video: '/api/v1/video/update_video',
    delete_video: '/api/v1/video/delete_video',
    remove_from_playlist: '/api/v1/video/delete_video_from_playlist',
    edit_playlist: '/api/v1/playlist/update_playlist',
    get_videos: '/api/v1/video/get_videos',
  },
  post: {
    add_post_media: '/api/v1/post/post_media',
    add_post: '/api/v1/post/create_post',
    delete_post: '/api/v1/post/delete_post',
    get_post: '/api/v1/post/get_posts',
    comment_on_post: '/api/v1/post/comment_on_post',
    like_unlike: '/api/v1/post/post_like_unlike',
    post_by_id: '/api/v1/post/get_post_by_id',
    get_Comments_On_Post: '/api/v1/post/get_post_comments',
  },
  faq: {
    // category
    get_faq_categories: '/api/v1/faq/parent_category/get_parent_categories',
    get_parent_categories_for_dropdown:
      '/api/v1/faq/parent_category/get_parent_categories_for_dropdown',
    add_edit_parent_category: '/api/v1/faq/parent_category/add_edit_parent_category',
    change_parent_category_status: '/api/v1/faq/parent_category/change_parent_category_status',

    // sub category
    get_sub_categories: '/api/v1/faq/sub_category/get_sub_categories',
    get_sub_categories_for_dropdown: '/api/v1/faq/sub_category/get_sub_categories_for_dropdown',
    add_edit_sub_category: '/api/v1/faq/sub_category/add_edit_sub_category',
    change_sub_category_status: '/api/v1/faq/sub_category/change_sub_category_status',

    // FAQs
    get_faqs: '/api/v1/faq/get_faqs',
    add_edit_faq: '/api/v1/faq/add_edit_faq',
    change_faq_status: '/api/v1/faq/change_faq_status',
    upload_faq_media: '/api/v1/faq/upload_faq_media',

    // member side
    search_faq: '/api/v1/faq/search_faq',
    get_faq_by_category: '/api/v1/faq/get_faq_by_category',
    get_faq_by_id: '/api/v1/faq/get_faq_by_id',
  },
  feedkback: {
    upload_feedback_media: '/api/v1/feedback/upload_feedback_media',
    add_feedback: '/api/v1/feedback/add_feedback',
    get_feedback: '/api/v1/feedback/get_feedback',
    get_categories: '/api/v1/feedback/get_categories',
    get_sub_categories: '/api/v1/feedback/get_sub_categories',
  },
  content: {
    get_page_content: '/api/v1/admin/get_page_content',
  },
  admin: {
    get_user_activity_analytics: '/api/v1/admin/get_user_activity_analytics',
    get_user_analytics: '/api/v1/admin/get_user_analytics',
    get_personal_members_list: '/api/v1/admin/get_personal_members_list',
    get_analytics_dropdown: '/api/v1/admin/get_analytics_dropdown',
    get_user_daily_activity_details: '/api/v1/admin/get_user_daily_activity_details',
    get_user_intake_nutrition_details: '/api/v1/admin/get_user_intake_nutrition_details',
    get_user_recent_activity_details: '/api/v1/admin/get_user_recent_activity_details',
    get_user_feature_breakdown_details: '/api/v1/admin/get_user_feature_breakdown_details',
    get_analytics_filters: '/api/v1/admin/get_analytics_filters',
    get_total_users_details: '/api/v1/admin/get_total_users_details',
    get_dau_details: '/api/v1/admin/get_dau_details',
    get_wau_details: '/api/v1/admin/get_wau_details',
    get_mau_details: '/api/v1/admin/get_mau_details',
    get_paid_users_details: '/api/v1/admin/get_paid_users_details',
    get_active_users_actions_details: '/api/v1/admin/get_active_users_actions_details',
    get_new_users_details: '/api/v1/admin/get_new_users_details',
    get_user_activations_details: '/api/v1/admin/get_user_activations_details',
    get_inactivity_buckets_details: '/api/v1/admin/get_inactivity_buckets_details',
    get_feature_usage_details: '/api/v1/admin/get_feature_usage_details',
    get_mrr_details: '/api/v1/admin/get_mrr_details',
    get_retention_cohort_details: '/api/v1/admin/get_retention_cohort_details',
    get_retention_by_type_details: '/api/v1/admin/get_retention_by_type_details',
    get_top_friction_events_details: '/api/v1/admin/get_top_friction_events_details',
    getCampaigns: '/api/v1/admin/get_campaigns',
    updateCampaign: '/api/v1/admin/update_campaign',
    change_campaign_status: '/api/v1/admin/change_campaign_status',
    get_referral_history: '/api/v1/admin/get_referral_history',
    get_qualification_rules: '/api/v1/admin/get_qualification_rules',
  },

  tickets: {
    upload_ticket_media: '/api/v1/faq/support_ticket/upload_ticket_media',
    generate_ticket: '/api/v1/faq/support_ticket/generate_ticket',
    get_support_tickets: '/api/v1/faq/support_ticket/get_support_tickets',
    add_ticket_reply: '/api/v1/faq/support_ticket/add_ticket_reply',
  },
  get_statuses: '/api/v1/get_statuses',
  get_subscriptions: '/api/v1/subscription/get_subscriptions',
  cancel_subscription: '/api/v1/subscription/cancel_subscription',
  reactivate_subscription: '/api/v1/subscription/reactivate_subscription',

  get_subscriptions_solo: '/api/v1/stripe/get_subscriptions',
  cancel_subscription_solo: '/api/v1/stripe/cancel_subscription',
  reactivate_subscription_solo: '/api/v1/stripe/reactivate_subscription',
  start_trial: '/api/v1/stripe/start_trial',

  system_errors: '/api/system_errors',

  chatify: {
    get_contacts: `/chatify/api/getContacts`,
    get_messages: `/chatify/api/fetchMessages`,
    get_user: `/chatify/api/idInfo`,
    send_message: `/chatify/api/sendMessage`,
    conversation_seen: `/chatify/api/makeSeen`,
    create_group: '/chatify/api/create_group',
    create_public_group: '/chatify/api/create_public_group',
    get_gym_users: '/api/v1/gym/get_gym_users',
    group_trigger: `/chatify/api/group_typing`,
    search_user_admin: `/chatify/api/search`,
    get_gym_remaining_users: '/api/v1/gym/get_gym_remaining_users',
    add_member_in_group: '/chatify/api/add_member_in_group',
    remove_leave_group: '/chatify/api/remove_group_member',
    get_public_groups: '/api/v1/gym/get_public_groups_by_gym_id',
    add_to_fav: '/chatify/api/star',
  },
  training_classes: {
    add_class_video: '/api/v1/training_classes/upload_class_media',
    add_class: '/api/v1/training_classes/add',
    get_classes: '/api/v1/training_classes/get_training_classes',
    accept_reject_request: '/api/v1/training_classes/action_on_invite_status',
    delete_class: '/api/v1/training_classes/delete_training_classes',
    get_class_by_id: '/api/v1/training_classes/get_training_class_detail',
    send_request: '/api/v1/training_classes/request_a_class',
    get_requests: '/api/v1/training_classes/get_class_requests',
    accept_reject_class_request: '/api/v1/training_classes/action_on_request',
    invite_members_to_class: '/api/v1/training_classes/invite_members',
  },

  report: {
    upload_report_media: 'api/v1/report/upload_media',
    create_report: '/api/v1/report/add',
    get_reports: '/api/v1/report/get_reports',
  },

  program: {
    add_program: '/api/v1/program/add',
    edit_program: '/api/v1/program/edit',

    get_programs: '/api/v1/program/get_programs',
    get_my_programs: '/api/v1/program/get_my_programs',
    delete_program: '/api/v1/program/delete_program_request',
    delete_program_final: '/api/v1/program/delete_program',
    reject_delete_request: '/api/v1/program/delete_program_request_cancel',

    delete_parent_program: '/api/v1/program/delete_parent_program',
    get_personal_member: '/api/v1/program/get_my_members',
    assign_members_to_program: '/api/v1/program/assign_members',
    unassign_members_to_program: '/api/v1/program/unassign_members',
    get_program_details: 'api/v1/program/get_program_by_id',
    delete_week: 'api/v1/program/delete_week',
    mark_as_active: 'api/v1/program/mark_as_active',
    get_program_members: 'api/v1/program/get_program_members',
    add_comment_media: 'api/v1/program/upload_comment_media',
    add_comment: 'api/v1/program/save_comment',
    get_comments_by_id: 'api/v1/program/get_comments',
    get_trainers_personal_member: '/api/v1/program/get_my_trainers',
    assign_trainer_to_program: '/api/v1/program/assign_trainer',
    unassign_trainer_to_program: '/api/v1/program/unassign_trainer',
    send_request_to_members: '/api/v1/program/send_request_to_members',
    release_program: '/api/v1/program/ralease_program',
    send_changes_for_approval: '/api/v1/program/send_changes_for_approval',
    add_note_to_program: '/api/v1/program/save_program_notes',
    get_trainer_programs_for_members: '/api/v1/member_program/get_programs',
    send_change_request_to_trainer: 'api/v1/member_program/send_change_request_to_trainer',
    mark_as_approve: 'api/v1/member_program/mark_as_approved',

    get_program_details_by_member: 'api/v1/member_program/get_program_by_id',

    // SET
    add_comment_media_set: 'api/v1/program/sets_upload_comment_media',
    add_comment_set: 'api/v1/program/save_sets_comment',
    get_comments_by_id_set: 'api/v1/program/get_sets_comments',

    // MARK AS COMPLETE
    mark_as_complete_exercise: 'api/v1/member_program/exercise_mark_as_complete',
    mark_as_complete_set: 'api/v1/member_program/sets_mark_as_complete',

    // PLATES
    add_plate_program: '/api/v1/plates/add',
    edit_plate_program: '/api/v1/plates/edit',
    get_plate_programs: '/api/v1/plates/get_programs',
    get_plate_program_details: '/api/v1/plates/get_program_by_id',
    get_nutrition_dropdown: '/api/v1/plates/get_nutritions',
    get_nutrition_units: '/api/v1/plates/get_units',
    send_request_to_member_plates: '/api/v1/plates/send_request_to_members',
    get_trainer_plates_for_member: '/api/v1/member_plates/get_programs',
    add_note_to_plate: '/api/v1/plates/save_program_notes',
    get_plate_detail_by_member: 'api/v1/member_plates/get_program_by_id',
    mark_as_approve_plate: 'api/v1/member_plates/mark_as_approved',
    send_change_request_to_trainer_plate: 'api/v1/member_plates/send_change_request_to_trainer',
    get_plate_members: 'api/v1/plates/get_program_members',
    send_changes_for_approval_plate: '/api/v1/plates/send_changes_for_approval',
    mark_as_active_plate: 'api/v1/plates/mark_as_active',
    delete_week_plate: 'api/v1/plates/delete_week',
    get_my_programs_plate: '/api/v1/member_plates/get_my_programs',
    get_food_items: '/api/v1/member_plates/get_food_items',
    get_dish_items: '/api/v1/member_plates/get_dishes',
    get_units: '/api/v1/plates/get_units',
    get_units_with_nutrients: '/api/v1/plates/get_units_with_nutritions',

    get_day_nutritions: '/api/v1/plates/get_program_day_nutritions',
    get_day_all_nutrients: '/api/v1/plates/get_program_day_all_nutritions',
    submit_food_log: '/api/v1/member_plates/day_food_log',
    delete_food_log: '/api/v1/member_plates/delete_day_food_log',
    add_food_item: '/api/v1/member_plates/add_fooditem',
    add_dish_item: '/api/v1/member_plates/add_dish',

    // FOOD LOG COMMENTS
    add_comment_media_log: '/api/v1/member_plates/dish_log_upload_comment_media',
    add_comment_log: '/api/v1/member_plates/save_dish_log_comment',
    get_comments_by_id_log: '/api/v1/member_plates/get_dish_log_comments',

    // DASHBOARD APIS
    get_programs_for_dropdown: '/api/v1/dashboard/get_my_programs_for_dropdown',
    get_program_progress: '/api/v1/dashboard/get_program_progress',
    get_plates_for_dropdown: '/api/v1/dashboard/get_my_nutrition_programs_for_dropdown',
    get_plate_progress: '/api/v1/dashboard/get_program_nutrition_progress',
    get_library_highlights: '/api/v1/dashboard/get_library_highlights',

    // PROGRESS NOTES

    add_progress_media: '/api/v1/dashboard/progress_upload_note_media',
    add_progress_note: 'api/v1/dashboard/save_progress_note',
    get_progress_notes: 'api/v1/dashboard/get_progress_notes',

    // CUSTOM GOALS
    get_custom_goal_category: 'api/v1/custom_goals/get_custom_goal_category',
    get_custom_goal_units: 'api/v1/custom_goals/get_goal_units',
    add_custom_goal: '/api/v1/custom_goals/create_custom_goal',
    get_custom_goals: 'api/v1/custom_goals/get_custom_goals',
    get_custom_goal_detail: 'api/v1/custom_goals/get_custom_goal_details',

    // CUSTOM GOAL ENTRIES

    add_custom_goal_entry: '/api/v1/custom_goals/save_milestone_entry',
    get_custom_goal_entries: '/api/v1/custom_goals/get_milestone_entry_custom_goal',
    get_milestone_by_goal: '/api/v1/custom_goals/get_milestone_by_goal_id',
    upload_milestone_media: '/api/v1/custom_goals/uplaod_milestone_entry_media',

    // PROGRESS NOTES RECENT ACHEIVEMENTS

    get_recent_achievements: '/api/v1/custom_goals/get_recent_achievements',
    get_quick_notes: '/api/v1/custom_goals/get_quick_stats',

    // SETTING DASHBOARD
    get_dashboard_setting: '/api/v1/dashboard/get_streak_type',
    set_train_streak: '/api/v1/dashboard/update_train_streak_type',
    set_plate_streak: '/api/v1/dashboard/update_plate_streak_type',

    // BREAKDOWN OF NUTRIENTS
    get_food_breakdown: '/api/v1/member_plates/get_food_breakdown',
    get_dish_breakdown: '/api/v1/member_plates/get_dish_breakdown',

    // TIME ZONE
    update_time_zone: '/api/v1/member/update_timezone',
    get_timezones: '/api/v1/get_timezones',

    // LIBRARY
    get_exercises_dashboard: '/api/v1/library/exercise_tab/get_exercise_dashboard_cards',
    add_exercise_to_library: '/api/v1/program/exercise_add_to_library',
    add_program_to_library: '/api/v1/program/program_add_to_library',
    check_exercise_exists: '/api/v1/program/check_exercise_exists',
    // PIN ENTITY
    pin_entity: '/api/v1/member_program/pin_entity',
  },
  library: {
    add_library_program: '/api/v1/library/exercise_tab/add_program',
    edit_library_program: '/api/v1/library/exercise_tab/edit_program',
    delete_library_program: '/api/v1/library/exercise_tab/delete_program',
    get_library_programs: '/api/v1/library/exercise_tab/get_member_programs',
    get_library_program_details: '/api/v1/library/exercise_tab/get_program_by_id',
    delete_week: '/api/v1/library/exercise_tab/delete_week',
    save_individual_exercise: '/api/v1/library/exercise_tab/save_exercise',
    get_individual_exercises: '/api/v1/library/exercise_tab/get_exercises',
    get_all_exercises: '/api/v1/library/exercise_tab/get_exercises_for_activity_bundle',
    get_individual_exercise_detail: '/api/v1/library/exercise_tab/get_exercise_by_id',

    delete_individual_exercise: '/api/v1/library/exercise_tab/delete_exercise',
    // NOTES COMMENTS
    get_workout_comments: '/api/v1/library/exercise_tab/get_exercise_comments',
    add_comment_media: 'api/v1/library/exercise_tab/upload_exercise_comment_media',
    add_comment: 'api/v1/library/exercise_tab/save_exercise_comment',

    // SET COMMENTS
    add_set_comment_media: 'api/v1/library/exercise_tab/sets_upload_comment_media',
    add_set_comment: 'api/v1/library/exercise_tab/save_sets_comment',
    get_set_comments: '/api/v1/library/exercise_tab/get_sets_comments',

    // TRAINER PROGRAMS
    get_library_trainer_programs: '/api/v1/library/exercise_tab/get_trainer_programs',

    // ACTIVITY BUNDLES
    get_activity_bundles: '/api/v1/library/exercise_tab/get_activity_bundles',
    add_activity_bundle: '/api/v1/library/exercise_tab/add_exercises_to_bundle',
    get_activity_bundle_details: '/api/v1/library/exercise_tab/get_activity_bundle_by_id',
    delete_activity_bundle: '/api/v1/library/exercise_tab/delete_activity_bundle',

    // LIBRARY NUTRITION
    get_nutrition_dashboard: '/api/v1/library/nutrition_tab/get_nutrition_dashboard_cards',
    // MEMBER PROGRAMS
    get_library_programs_plate: '/api/v1/library/nutrition_tab/get_member_plate_programs',
    delete_library_program_plate: '/api/v1/library/nutrition_tab/delete__plate_program',
    add_library_program_plate: '/api/v1/library/nutrition_tab/add_plate_program',
    edit_library_program_plate: '/api/v1/library/nutrition_tab/edit_plate_program',
    get_library_program_details_plate: '/api/v1/library/nutrition_tab/get_plate_program_by_id',
    delete_week_plate: '/api/v1/library/nutrition_tab/delete_plate_week',

    // TRAINER PROGRAM PLATE
    get_library_trainer_programs_plate: '/api/v1/library/nutrition_tab/get_trainer_plate_programs',
    get_log_comments: '/api/v1/library/nutrition_tab/get_dish_log_comments',

    // INDIVIDUAL FOODS ITEMS
    get_all_nutrients: '/api/v1/library/nutrition_tab/get_all_nutritions',
    add_food_item: '/api/v1/library/nutrition_tab/add_fooditem',
    get_all_food_items: '/api/v1/library/nutrition_tab/get_food_items',
    delete_food_item: '/api/v1/library/nutrition_tab/delete_food_item',
    get_food_item_detail: '/api/v1/library/nutrition_tab/get_food_item_by_id',

    // DISH ITEMS

    add_dish: '/api/v1/library/nutrition_tab/add_dish',
    get_all_dishes: '/api/v1/library/nutrition_tab/get_dishes',
    get_all_dishes_meal: '/api/v1/library/nutrition_tab/get_dishes_for_meal',
    get_dish_breakdown: '/api/v1/library/nutrition_tab/get_dish_breakdown',
    delete_dish: '/api/v1/library/nutrition_tab/delete_dish',
    get_dish_detail: '/api/v1/library/nutrition_tab/get_dish_by_id',

    // INDIVIDUAL NUTRIENTS PLAN
    add_nutrition_plan: '/api/v1/library/nutrition_tab/add_edit_nutrition_plan',
    get_all_nutrient_plan: '/api/v1/library/nutrition_tab/get_nutrition_plan',
    delete_nutrition_plan: '/api/v1/library/nutrition_tab/delete_nutrition_plan',

    // MEALS
    get_meals: '/api/v1/library/nutrition_tab/get_meals',
    add_meals: '/api/v1/library/nutrition_tab/add_edit_meal',
    get_meal_details: '/api/v1/library/nutrition_tab/get_meal_by_id',
    delete_meals: '/api/v1/library/nutrition_tab/delete_meal',

    // CUSTOM TAB

    add_custom_tab: '/api/v1/library/custom_tab/add_custom_tab',
    get_custom_tab: '/api/v1/library/custom_tab/get_custom_tabs',
    delete_custom_tab: '/api/v1/library/custom_tab/delete_custom_tab',

    // IMAGES TAB

    get_custom_media: '/api/v1/library/custom_tab/get_media_custom_tabs',
    add_custom_media: '/api/v1/library/custom_tab/upload_media_custom_tabs',
    delete_custom_media: '/api/v1/library/custom_tab/delete_media_custom_tabs',

    // IMPORT EXPORT
    add_program_to_library: '/api/v1/program/program_add_to_library',
    check_program_exist: '/api/v1/program/check_program_exists',
    add_program_plate_to_library: '/api/v1/program/program_add_to_library',
    check_program_plate_exist: '/api/v1/program/check_program_exists',
    add_plate_program_to_library: '/api/v1/plates/program_add_to_library',
    check_plate_program_exist: '/api/v1/plates/check_program_exists',
    add_nutrient_plan_to_library: '/api/v1/plates/nutrition_plan_add_to_library',

    get_all_workout_programs: '/api/v1/program/get_my_library_programs',
    import_program: '/api/v1/program/import_program',

    get_all_workouts: '/api/v1/program/get_my_library_exercises',
    import_workout: '/api/v1/program/import_exercise',

    get_all_bundles: '/api/v1/program/get_my_library_activity_bundles',
    import_bundle: '/api/v1/program/import_activity_bundle',

    // PLATE PROGRAMS
    get_all_plate_programs: '/api/v1/plates/get_my_library_programs',
    import_plate_program: '/api/v1/plates/import_program',
    get_all_nutrition_plans: '/api/v1/plates/get_my_nutrition_plans',
    import_nutrition_plan: '/api/v1/plates/import_nutrition_plan',

    // FOOD ITEM IMPORT
    get_all_foods: '/api/v1/member_plates/get_my_library_fooditems',
    import_food: '/api/v1/member_plates/import_fooditem',

    // DISH IMPORT
    get_all_dish: '/api/v1/member_plates/get_my_library_dishes',
    import_dish: '/api/v1/member_plates/import_dish',

    library_highlights_toggle: '/api/v1/library/custom_tab/add_to_library_highlights',
    move_or_copy: '/api/v1/library/custom_tab/copy_or_move_to_custom_tab',

    // LOG HISTORY
    get_logs_history: '/api/v1/member_plates/get_my_logs_history',
    log_toggle: '/api/v1/member_plates/log_mark_as_favorite',
    replicate_food_log: '/api/v1/member_plates/replicate_food_log',

    // GET HISTORY NOTES
    get_history_notes: '/api/v1/member_program/get_all_exercises_for_notes_history',
    import_history_note: '/api/v1/member_program/import_exercise_notes',
    import_set_history_note: '/api/v1/member_program/import_exercise_set_notes',
    get_set_history_notes: '/api/v1/member_program/get_all_sets_for_notes_history',
    get_notes_history: '/api/v1/member_program/get_all_notes_history',
    get_all_notes_food_history: '/api/v1/member_program/get_all_notes_food_history',
    import_notes_history: '/api/v1/member_program/import_notes_history',
    import_food_notes_history: '/api/v1/member_program/import_food_notes_history',
    delete_comment_by_type: '/api/v1/member_program/delete_comment_by_type',

    // FOOD ITEM COMMENTS
    // save_food_item_comment:"member_plates/save_food_item_comment",
    // food_item_upload_comment_media:"member_plates/food_item_upload_comment_media",
    // get_food_item_comments:"member_plates/get_food_item_comments",
    // delete_food_item_comment:"member_plates/delete_food_item_comment",

    // LIBRARY NUTRITION FOOD ITEM COMMENTS
    save_nutrition_food_item_comment: 'api/v1/library/nutrition_tab/save_food_item_comment',
    upload_nutrition_food_item_comment_media:
      'api/v1/library/nutrition_tab/upload_food_item_comment_media',
    get_nutrition_food_item_comments: 'api/v1/library/nutrition_tab/get_food_item_comments',
    delete_nutrition_food_item_comment: 'api/v1/library/nutrition_tab/delete_food_item_comment',

    // LIBRARY NUTRITION DISH ITEM COMMENTS
    save_nutrition_dish_comment: 'api/v1/library/nutrition_tab/save_dish_comment',
    upload_nutrition_dish_comment_media: 'api/v1/library/nutrition_tab/dish_upload_comment_media',
    get_nutrition_dish_comments: 'api/v1/library/nutrition_tab/get_dish_comments',
    delete_nutrition_dish_comment: 'api/v1/library/nutrition_tab/delete_dish_comment',

    // LIBRARY NUTRITION MEAL COMMENTS

    save_nutrition_meal_comment: 'api/v1/library/nutrition_tab/save_meal_comment',
    upload_nutrition_meal_comment_media: 'api/v1/library/nutrition_tab/meal_upload_comment_media',
    get_nutrition_meal_comments: 'api/v1/library/nutrition_tab/get_meal_comments',
    delete_nutrition_meal_comment: 'api/v1/library/nutrition_tab/delete_meal_comment',
  },
  newsletter: {
    get_newsletters_categories: '/api/v1/admin/get_newsletter_categories',
    get_newsletters: '/api/v1/admin/get_newsletters',
    send_newletter: '/api/v1/admin/send_newsletters',
    delete_newsletter: '/api/v1/admin/delete_newsletter',
  },
  launchlist: {
    get_launch_lists: '/api/v1/admin/get_launch_lists',
    delete_launch_list: '/api/v1/admin/delete_launch_list',
  },
  fooddatabase: {
    search: '/api/v1/fatsecret/search',
    barcode: '/api/v1/fatsecret/barcode',
    import_external_foods_in_library: '/api/v1/fatsecret/import_external_foods_in_library',
    import_external_foods: '/api/v1/fatsecret/import_external_foods',
    food_by_id: '/api/v1/fatsecret/food_by_id',
  },
};
